import { axiosInstance } from "@/api/axios-instance";
import type { SessionData } from "@/types/collaborative";

/**
 * Session API Response structure
 */
interface SessionRegisterResponse {
  status: string;
  data: SessionData;
  statusCode: number;
}

/**
 * Register a new session for the authenticated user
 * This should be called after successful login to obtain a session ID
 *
 * @returns Promise with session data (sessionId and expiresIn)
 * @throws Error if registration fails
 */
export const registerSession = async (): Promise<SessionData> => {
  try {
    const response = await axiosInstance.post<SessionRegisterResponse>(
      "/api/auth/session/register",
    );

    if (response.data.status === "success" && response.data.data) {
      return response.data.data;
    }

    throw new Error("Invalid session registration response");
  } catch (error) {
    console.error("Failed to register session:", error);
    throw error;
  }
};

/**
 * Renew/extend an existing session
 * This should be called when user chooses to continue using the application
 * after receiving a session_expiring warning
 *
 * @returns Promise with new session data
 * @throws Error if renewal fails
 */
export const renewSession = async (): Promise<SessionData> => {
  try {
    // According to the API spec, renewing is the same as registering
    // It creates a new session with a new session ID
    return await registerSession();
  } catch (error) {
    console.error("Failed to renew session:", error);
    throw error;
  }
};

/**
 * Refresh an existing session to extend its expiration time
 * This requires a valid session ID in the request header (automatically added by axios interceptor)
 *
 * Unlike registerSession which creates a new session ID, this method extends
 * the expiration time of the current session while keeping the same session ID
 *
 * @returns Promise with session data (same sessionId with updated expiresIn)
 * @throws Error if refresh fails (401: invalid/missing session, 403: session not owned by current user, 500: server error)
 */
export const refreshSession = async (): Promise<SessionData> => {
  try {
    const response = await axiosInstance.post<SessionRegisterResponse>("/api/auth/session/refresh");

    if (response.data.status === "success" && response.data.data) {
      return response.data.data;
    }

    throw new Error("Invalid session refresh response");
  } catch (error) {
    console.error("Failed to refresh session:", error);
    throw error;
  }
};

/**
 * Session Service exports
 */
const SessionService = {
  registerSession,
  renewSession,
  refreshSession,
};

export default SessionService;
